<template>
    <div>
        <div class="mb-6 font-sans">
            <div v-for="(placement, placementIndex) in optionGroups"
                :key="placement.placement.code"
                :class="{
                    'mt-6': placementIndex > 0,
                }"
            >
                <div v-for="(group, groupIndex) in placement.groups"
                    :key="`${placement.placement.code}-${group.description.definition}`"
                    :class="{
                        'mt-4': groupIndex > 0,
                    }"
                >
                    <div class="mb-2 flex justify-between items-center">
                        <label class="text-sm font-bold font-gotham">
                            {{ getLabelText(placement.placement.name, group.description.definition) }}
                        </label>

                        <size-guide-button v-if="groupIndex === 0 && placementIndex === 0" />
                    </div>

                    <text-button-group
                        :option-name="placement.placement.code"
                        :options="group.options"
                        :selected-value="group.selectedValue"
                        @selection-changed="selectValue(placement, $event)"
                        :has-long-button-text="isEMEA"
                    />
                </div>

                <p
                    v-if="placement.backordered"
                    class="mt-4 italic"
                >
                    {{placement.backordered}}
                </p>
            </div>
        </div>

        <base-checkbox
            :model-value="showIndividualOptions"
            checkbox-id="differentSize"
            @checkboxChecked="toggleIndividualOptions"
        >
            {{ $t('DIFFERENT_SIZE') }}
        </base-checkbox>
    </div>
</template>

<script>
    import BaseCheckbox from './BaseCheckbox.vue';
    import SizeGuideButton from './SizeGuideButton.vue';
    import TextButtonGroup from './TextButtonGroup.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            BaseCheckbox,
            SizeGuideButton,
            TextButtonGroup,
        },

        data() {
            return {
                showIndividualOptions: false,
            };
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            /**
             * Get all placements visible in this blueprint group.
             */
            visiblePlacements() {
                const store = this.customizerStore;

                const groups = store.visiblePlacementsByGroup;

                const placements = this.itemData.code in groups ? groups[this.itemData.code] : [];

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        selectedIndependentValue: selected ? selected.placementIndependentCode : null,
                        ...placement,
                    };
                });
            },

            /**
             * Do all placements have the same size selected?
             */
            isSameSize() {
                let selected = null;

                for (let i = 0; i < this.visiblePlacements.length; i += 1) {
                    const placementValue = this.visiblePlacements[i].selectedIndependentValue;

                    if (selected == null) {
                        selected = placementValue;
                    } else if (selected !== placementValue) {
                        return false;
                    }
                }

                return true;
            },

            /**
             * Within each placement, create groups based on individual component definitions.
             */
            optionGroups() {
                const groups = [];

                this.visiblePlacements.forEach((placement) => {
                    const placementGroups = this.viewController.groupByDefinitions(placement, {
                        includeUnavailable: true,
                    });

                    // Reform selection groups based on what TextButtonGroup component expects to see.

                    // eslint-disable-next-line arrow-body-style
                    const niceGroups = placementGroups.map((group) => ({
                        description: group.description,
                        options: group.options.filter((o) => !o.placeholder).map((o) => ({
                            hidden: o.hidden,
                            available: o.available,
                            allowed: o.allowed,
                            selected: o.selected,
                            value: o.option.code,
                            label: this.getButtonLabelText(o.option),
                            definition: o.option.definition,
                        })),
                        selectedValue: group.options.find((o) => o.selected)?.option?.code || '',
                    }));

                    // Check the availability.
                    const selected = this.viewController.selectedAtPlacement(placement.code);
                    let backordered = null;

                    if (selected) {
                        const backorderDate = this.customizerStore.isBackordered(selected);
                        // It should not be possible to run out of stuff without an availability date known.
                        if (backorderDate) {
                            // I liked how easy moment was...
                            // JavaScript timezone handling is horrendous.
                            const formatter = new Intl.DateTimeFormat(this.customizerStore.options.locale, {
                                dateStyle: 'short',
                            });

                            const when = formatter.format(new Date(backorderDate));

                            backordered = this.$t('BACKORDERED', {
                                date: when,
                            });
                        }
                    }

                    groups.push({
                        placement,
                        backordered,
                        groups: niceGroups,
                    });

                    this.checkForBackordered(groups);
                });

                if (!this.showIndividualOptions) {
                    return groups.length > 0 ? groups.slice(0, 1) : [];
                }

                return groups;
            },

            sizeTranslations() {
                return this.customizerStore.sizeTranslations.dictionary.item;
            },

            locale() {
                return this.customizerStore.options?.locale;
            },

            isEMEA() {
                // Used to display larger buttons for locales that display full, untranslated sizes in the size buttons.
                return this.locale.includes('IE') || this.locale.includes('SE') || this.locale.includes('GB')
                    || this.locale.includes('BE') || this.locale.includes('FR') || this.locale.includes('DE');
            },

            gender() {
                return this.customizerStore.product?.gender;
            },
        },

        watch: {
            viewController() {
                // Recalculate status after the Reset button click.
                this.showIndividualOptions = !this.isSameSize;
            },
        },

        mounted() {
            // Setup initial selection state.
            this.showIndividualOptions = !this.isSameSize;

            // Override by options.
            if (this.customizerStore.preferDifferentSizes()) {
                this.showIndividualOptions = true;
            }
        },

        methods: {
            toggleIndividualOptions(isChecked) {
                this.showIndividualOptions = isChecked;

                if (!this.showIndividualOptions) {
                    this.resetRightShoe();
                }
            },

            selectValue(placement, option) {
                this.customizerStore.undoCheckpoint();

                if (this.showIndividualOptions) {
                    if (this.viewController.updateSimilarComponent(placement.placement.code, option.option.definition, option.option.value) == null) {
                        // Is there a safe selection? Select a blank width and retry.
                        if (this.viewController.updateSimilarComponent(placement.placement.code, 'Width', 'Width-Blank')) {
                            this.viewController.updateSimilarComponent(placement.placement.code, option.option.definition, option.option.value);
                        }
                    }
                } else {
                    this.visiblePlacements.forEach((anyPlacement) => {
                        if (this.viewController.updateSimilarComponent(anyPlacement.code, option.option.definition, option.option.value) == null) {
                            // Is there a safe selection? Select a blank width and retry.
                            if (this.viewController.updateSimilarComponent(anyPlacement.code, 'Width', 'Width-Blank')) {
                                this.viewController.updateSimilarComponent(anyPlacement.code, option.option.definition, option.option.value);
                            }
                        }
                    });
                }
            },

            /**
             * Makes the right shoe size and width to match the left one.
             */
            resetRightShoe() {
                let sizeLeft = null;
                let widthLeft = null;

                // Figure out what's currently selected on the left side.
                const leftSize = this.viewController.selectedAtPlacement('LeftSizeWidth');
                if (leftSize?.custom?.['fj-size-value']) {
                    sizeLeft = leftSize?.custom?.['fj-size-value'];
                }
                if (leftSize?.custom?.['fj-width-value']) {
                    widthLeft = leftSize?.custom?.['fj-width-value'];
                }

                if (sizeLeft != null && widthLeft != null) {
                    this.viewController.updateSimilarComponent('RightSizeWidth', 'Size', `Size-${sizeLeft.trim()}`);
                    this.viewController.updateSimilarComponent('RightSizeWidth', 'Width', `Width-${widthLeft.trim()}`);
                }
            },

            getLabelText(placementName, definition) {
                let labelText = definition === 'Width' ? this.$t('SELECT_WIDTH') : this.$t('SELECT_SIZE');

                if (this.showIndividualOptions) {
                    if (this.isLeftSize(placementName, definition)) {
                        labelText = this.$t('LEFT_SHOE_SIZE');
                    } else if (this.isRightSize(placementName, definition)) {
                        labelText = this.$t('RIGHT_SHOE_SIZE');
                    }
                }

                return labelText;
            },

            isLeftSize(string, definition) {
                const includesLeft = string.toLowerCase().includes('left');
                const isWidth = definition.toLowerCase().includes('width');

                return includesLeft && !isWidth;
            },

            isRightSize(string, definition) {
                const includesRight = string.toLowerCase().includes('right');
                const isWidth = definition.toLowerCase().includes('width');

                return includesRight && !isWidth;
            },

            getButtonLabelText(option) {
                let prettySize = '';

                if (option.definition === 'Size') {
                    let useName = option.name;

                    if (this.gender === 'F' && option.custom?.['fj-size-f-name']) {
                        useName = option.custom?.['fj-size-f-name'];
                    }

                    if (this.gender === 'M' && option.custom?.['fj-size-m-name']) {
                        useName = option.custom?.['fj-size-m-name'];
                    }

                    const matchingSize = this.sizeTranslations.find((size) => size['@description'] === useName);

                    if (matchingSize) {
                        const matchingSizeText = matchingSize['#text'];

                        // Given a size like "6.0 US", remove the country and trim trailing .0 to show whole numbers.
                        // Keep international sizes like 9.0 UK/10.0 US/43.0 EUR complex as they are...
                        prettySize = matchingSizeText.replace(/ US$/, '').replace(/\.0/g, '').trim();
                    }
                } else {
                    // Given a width like "Width" or "Width *", trim the just to the name.
                    prettySize = option.name.replace(/ *$/, '');
                }

                return prettySize;
            },

            checkForBackordered(groups) {
                const backordered = groups.find((group) => group.backordered !== null);

                if (backordered) {
                    this.customizerStore.setHasBackorderedSize(true);
                } else {
                    this.customizerStore.setHasBackorderedSize(false);
                    this.customizerStore.setHasBackorderedError();
                }
            },
        },
    };
</script>
